import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  api,
  clearSession,
  groupDirectory,
  setLogoutReason,
  openStream,
  type Contact,
  type DirectoryEntry,
  type Session,
} from './api.js';
import {
  acceptIdentityChange,
  decryptMessage,
  encryptMessage,
  ensureSession,
  hasIdentityChanged,
  hasSessionWith,
  safetyNumber,
} from './crypto/engine.js';
import {
  loadConversation,
  loadLastMessage,
  markMessagesRead,
  saveMessage,
  takeUnconfirmed,
  type StoredMessage,
} from './crypto/store.js';
import {
  Avatar,
  BackIcon,
  CheckIcon,
  ShieldIcon,
  CopyIcon,
  CloseIcon,
  PhoneIcon,
  SendIcon,
  SettingsIcon,
  VideoIcon,
  formatDay,
  formatListTime,
  formatTime,
} from './ui.js';
import { Settings } from './Settings.js';
import { CallOverlay } from './CallOverlay.js';
import { useCall } from './call.js';
import { decodePayload, encodePayload, type Payload } from './payload.js';
import { useT } from './i18n.js';
import { setUpPush } from './push.js';

/**
 * 1:1-Chat.
 *
 * Alles, was den Browser verlässt, ist Ciphertext. Der Klartext existiert in
 * IndexedDB auf diesem Gerät und im Speicher der Gegenstelle — sonst nirgends.
 * Insbesondere nicht auf dem Server: Dort liegt nur ein opaker Blob in einer
 * Warteschlange, der nach der Zustellung gelöscht wird.
 */
/**
 * Der Schluessel einer Gegenstelle hat sich geaendert.
 *
 * Eigene Fehlerklasse statt einer Textmeldung, damit der Aufrufer sie sicher
 * erkennt und die Geraete-ID mitbekommt — eine Zeichenkette zu vergleichen
 * waere fuer einen Sicherheitsfall zu wacklig.
 */
class IdentityChangedError extends Error {
  constructor(public readonly deviceId: string) {
    super('identity_changed');
  }
}

export function Chat({ session, onLogout }: { session: Session; onLogout: () => void }) {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [peer, setPeer] = useState<Contact | null>(null);
  const t = useT();
  const [messages, setMessages] = useState<StoredMessage[]>([]);
  const [previews, setPreviews] = useState<Record<string, StoredMessage | null>>({});
  const [unread, setUnread] = useState<Record<string, number>>({});
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fingerprint, setFingerprint] = useState<string | null>(null);
  /** Geräte, deren Schlüssel sich geändert hat und die noch nicht bestätigt sind. */
  const [keyAlert, setKeyAlert] = useState<string[]>([]);
  const [showSafety, setShowSafety] = useState(false);
  /**
   * Ausgewählte Nachrichten.
   *
   * Mehrere, nicht eine: Wer aufräumt, meint selten genau eine Zeile. Die
   * Auswahl beginnt mit langem Drücken; danach genügt ein Tippen, wie man es
   * von Telefon-Apps kennt.
   */
  const [selected, setSelected] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  // In Callbacks gebraucht, ohne sie bei jedem Gesprächswechsel neu zu bauen.
  const peerRef = useRef<Contact | null>(null);
  peerRef.current = peer;

  // Eingehende Envelopes tragen eine Geräte-ID; der Verlauf ist aber nach
  // Konto abgelegt. Diese Zuordnung übersetzt zwischen beidem.
  const deviceToAccount = useRef<Map<string, string>>(new Map());

  // Die eigenen ANDEREN Geräte. Sie bekommen Kopien der eigenen Nachrichten,
  // damit der Verlauf auf allen Geräten derselbe ist.
  const ownDevices = useRef<string[]>([]);

  // Der Abholzyklus braucht den aktuellen Stand, ohne bei jeder Änderung neu
  // aufgebaut zu werden — sonst würde der WebSocket-Listener ständig neu
  // registriert und Signale gingen verloren.
  const contactsRef = useRef<Contact[]>([]);
  contactsRef.current = contacts;

  /**
   * Verschlüsselt einen Payload und stellt ihn zu — der gemeinsame Weg für
   * Chatnachrichten UND Anrufsignale. Dass beides identisch aussieht, ist
   * Absicht: Der Server kann nicht unterscheiden, ob geschrieben oder
   * telefoniert wird.
   */
  const sendToDevices = useCallback(
    async (toDeviceIds: string[], payload: Payload) => {
      const plaintext = encodePayload(payload);
      const timestamp = Date.now();
      const envelopes = [];

      // Je Gerät eine eigene Sitzung und eine eigene Verschlüsselung — das ist
      // Signal-Semantik, nicht Redundanz: Jedes Gerät hat eine eigene
      // kryptografische Identität (docs/01-architektur.md §6).
      for (const toDeviceId of toDeviceIds) {
        if (toDeviceId === session.deviceId) continue;

        // Bundle NUR holen, wenn noch keine Sitzung besteht. Der Server löscht
        // den One-Time-Prekey beim Herausgeben — ein Abruf je Nachricht würde
        // den Vorrat der Gegenstelle aufbrauchen, ohne ihn zu benutzen.
        if (!(await hasSessionWith(toDeviceId))) {
          const bundle = await api.preKeyBundle(session.token, toDeviceId);

          // Genau hier greift ein boesartiger Server an: Er liefert ein Bundle
          // mit SEINEM Identity Key statt dem der Gegenstelle und liest ab
          // dann mit. Ein bekannter, aber abweichender Schluessel ist deshalb
          // kein Randfall, sondern der Ernstfall — und darf nicht still
          // durchgehen. Siehe docs/03-bedrohungsmodell.md §5.
          if (await hasIdentityChanged(toDeviceId, bundle.identityKey)) {
            throw new IdentityChangedError(toDeviceId);
          }

          await ensureSession(bundle);
        }

        const { ciphertext, type } = await encryptMessage(toDeviceId, plaintext);

        envelopes.push({
          version: 1 as const,
          type,
          destinationDeviceId: toDeviceId,
          sourceDeviceId: session.deviceId,
          ciphertext,
          timestamp,
          messageId: crypto.randomUUID(),
        });
      }

      if (envelopes.length === 0) return;

      const { unknownDevices } = await api.send(session.token, envelopes);

      // Ein Gerät, das der Server nicht kennt, darf nicht still verschluckt
      // werden — sonst sieht es wie zugestellt aus und ist es nicht. Solange
      // mindestens ein Gerät erreicht wurde, gilt die Nachricht als gesendet.
      if (unknownDevices?.length === envelopes.length) {
        throw new Error(t('errNoDeviceReachable'));
      }
    },
    [session],
  );

  // Anrufsignale gehen an GENAU ein Gerät: Ein Gespräch läuft mit einem Gerät,
  // nicht mit einer Person. Deshalb hier kein Fan-out.
  const sendPayload = useCallback(
    (toDeviceId: string, payload: Payload) => sendToDevices([toDeviceId], payload),
    [sendToDevices],
  );

  const call = useCall(sendPayload, session.token);

  // Wie contactsRef: Der Abholzyklus braucht den aktuellen Anrufzustand, darf
  // aber nicht bei jeder Zustandsänderung neu gebaut werden.
  const callRef = useRef(call);
  callRef.current = call;

  // Schlüssel ist die Konto-ID, nicht die Geräte-ID: Ein Gespräch gehört zu
  // einer Person, auch wenn sie das Gerät wechselt.
  const refreshPreview = useCallback(async (accountId: string) => {
    const last = await loadLastMessage(accountId);
    setPreviews((prev) => ({ ...prev, [accountId]: last }));
  }, []);

  /* ── Kontakte ─────────────────────────────────────────────────────────── */

  useEffect(() => {
    api
      .directory(session.token)
      .then(async (res: { users: DirectoryEntry[] }) => {
        // Zuordnung Gerät -> Konto für eingehende Envelopes. Enthält bewusst
        // auch die eigenen Geräte, damit eine Nachricht von einem früheren
        // eigenen Gerät nicht als unbekannt gilt.
        deviceToAccount.current = new Map(
          res.users.map((u) => [u.device_id, u.account_id]),
        );

        ownDevices.current = res.users
          .filter((u) => u.account_id === session.accountId && u.device_id !== session.deviceId)
          .map((u) => u.device_id);

        const others = groupDirectory(res.users, session.accountId);
        setContacts(others);
        for (const contact of others) await refreshPreview(contact.accountId);
      })
      .catch((err) => setError(`${t('errContactsFailed')}: ${(err as Error).message}`));
  }, [session, refreshPreview]);

  /* ── Eingehende Nachrichten ───────────────────────────────────────────── */

  /**
   * Lesebestaetigung fuer alle noch nicht bestaetigten Nachrichten eines
   * Gespraechs. Geht an ALLE Geraete der Gegenseite — sonst zeigte ihr
   * Telefon gelesen und ihr Notebook nicht.
   */
  const confirmRead = useCallback(
    async (contact: Contact) => {
      const messageIds = await takeUnconfirmed(contact.accountId);
      if (messageIds.length === 0) return;

      await sendToDevices(contact.deviceIds, { kind: 'read', messageIds }).catch(() => {
        // Eine verlorene Bestaetigung ist ein kosmetischer Mangel und kein
        // Grund, das Abholen oder das Oeffnen des Gespraechs scheitern zu
        // lassen.
      });
    },
    [sendToDevices],
  );

  /**
   * Geräteliste der Gegenstelle auffrischen und zurückgeben.
   *
   * Die Liste vom Seitenaufruf ist eine Momentaufnahme. Richtet die
   * Gegenstelle danach ein neues Gerät ein — etwa die Android-App —, bliebe es
   * unsichtbar: Nachrichten kämen dort nie an, und ein Anruf klingelte nur auf
   * den alten Geräten. Beides ohne Fehlermeldung.
   *
   * Deshalb vor JEDER Zustellung, Nachricht wie Anruf. Der Aufruf kostet eine
   * Anfrage; ein Anruf, der ins Leere klingelt, kostet mehr.
   */
  const refreshDevices = useCallback(
    async (accountId: string): Promise<Contact> => {
      const current = await api.directory(session.token);

      deviceToAccount.current = new Map(current.users.map((u) => [u.device_id, u.account_id]));
      ownDevices.current = current.users
        .filter((u) => u.account_id === session.accountId && u.device_id !== session.deviceId)
        .map((u) => u.device_id);

      const refreshed = groupDirectory(current.users, session.accountId);
      setContacts(refreshed);

      const target = refreshed.find((c) => c.accountId === accountId);
      if (!target) throw new Error(t('errContactGone'));
      return target;
    },
    [session],
  );

  /** Anruf starten — mit aufgefrischter Geräteliste, damit alle klingeln. */
  const startCall = useCallback(
    async (contact: Contact, video: boolean) => {
      setError(null);
      try {
        const target = await refreshDevices(contact.accountId);
        await call.start(target.deviceIds, target.handle, video);
      } catch (err) {
        setError(`${t('errCallFailed')}: ${(err as Error).message}`);
      }
    },
    [refreshDevices, call],
  );

  const fetchMessages = useCallback(async () => {
    try {
      const { envelopes } = await api.receive(session.token);
      if (envelopes.length === 0) return;

      const delivered: string[] = [];
      const fresh: Record<string, number> = {};
      const touched = new Set<string>();
      let readReceipts = false;

      for (const envelope of envelopes) {
        if (!envelope.sourceDeviceId) continue;
        try {
          const raw = await decryptMessage(
            envelope.sourceDeviceId,
            envelope.type,
            envelope.ciphertext,
          );
          const payload = decodePayload(raw);
          delivered.push(envelope.messageId);

          // Der Envelope nennt das Absendergerät; abgelegt wird nach Konto.
          const fromAccount = deviceToAccount.current.get(envelope.sourceDeviceId);
          const from = fromAccount
            ? contactsRef.current.find((c) => c.accountId === fromAccount)
            : undefined;

          if (payload.kind === 'call') {
            // Anrufsignale landen nicht im Verlauf: Sie sind Steuerdaten, kein
            // Gesprächsinhalt, und ein Chat voller SDP-Blöcke wäre unbrauchbar.
            await callRef.current.handleSignal(
              envelope.sourceDeviceId,
              from?.handle ?? 'Unbekannt',
              payload,
            );
            continue;
          }

          // Lesebestaetigung der Gegenseite: eigene Nachrichten abhaken.
          if (payload.kind === 'read') {
            delivered.push(envelope.messageId);
            if (await markMessagesRead(payload.messageIds)) readReceipts = true;
            continue;
          }

          // Kopie einer eigenen Nachricht von einem anderen eigenen Geraet.
          if (payload.kind === 'sync') {
            // Nur von den EIGENEN Geraeten annehmen. Sonst koennte jeder
            // Kontakt Eintraege in beliebige fremde Gespraechsverlaeufe
            // schreiben, die dann aussehen, als haette man sie selbst
            // geschrieben.
            if (fromAccount !== session.accountId) {
              delivered.push(envelope.messageId);
              continue;
            }

            delivered.push(envelope.messageId);
            await saveMessage({
              id: payload.messageId,
              peerDeviceId: payload.peerAccountId,
              direction: 'out',
              text: payload.text,
              timestamp: payload.timestamp,
              status: 'sent',
            });
            touched.add(payload.peerAccountId);
            continue;
          }

          // Ohne bekanntes Konto ginge die Nachricht in einen Verlauf, den
          // keine Kontaktzeile öffnet — lieber als Geräte-ID ablegen, dann
          // taucht sie nach dem nächsten Verzeichnisabgleich richtig auf.
          const conversationKey = fromAccount ?? envelope.sourceDeviceId;

          await saveMessage({
            id: envelope.messageId,
            peerDeviceId: conversationKey,
            direction: 'in',
            text: payload.text,
            timestamp: envelope.timestamp,
            ...(payload.messageId ? { remoteId: payload.messageId } : {}),
          });

          touched.add(conversationKey);
          if (peerRef.current?.accountId !== conversationKey) {
            fresh[conversationKey] = (fresh[conversationKey] ?? 0) + 1;
          }
        } catch (err) {
          // Eine Nachricht, die sich nicht entschlüsseln lässt, wird NICHT
          // bestätigt — sie bliebe sonst unwiederbringlich verloren. Häufigste
          // Ursache: Der Absender hat seine Sitzung zurückgesetzt.
          console.error('Decryption failed', err);
        }
      }

      if (delivered.length === 0) return;

      // Erst nach erfolgreicher lokaler Speicherung löschen lassen.
      await api.acknowledge(session.token, delivered);

      if (Object.keys(fresh).length > 0) {
        setUnread((prev) => {
          const next = { ...prev };
          for (const [id, count] of Object.entries(fresh)) next[id] = (next[id] ?? 0) + count;
          return next;
        });
      }

      // Bestaetigen, wenn das Gespraech gerade offen ist. Ist es das nicht,
      // wurde die Nachricht nicht gelesen — eine Bestaetigung waere gelogen.
      const open = peerRef.current;
      if (open && touched.has(open.accountId)) void confirmRead(open);

      for (const accountId of touched) await refreshPreview(accountId);

      const current = peerRef.current;
      if (current && (touched.size > 0 || readReceipts)) {
        setMessages(await loadConversation(current.accountId));
      }
    } catch (err) {
      /*
       * Das Geraet wurde in der Konsole entfernt.
       *
       * Ohne diesen Zweig liefe der Client als Karteileiche weiter: Er holt
       * ab, bekommt nie etwas und zeigt keinen Grund. Das Token gilt bis zum
       * Ablauf — einen Widerruf gibt es nicht —, also muss der Client selbst
       * die Konsequenz ziehen.
       *
       * Die Schluessel bleiben liegen: Sie sind wertlos, aber ihr Loeschen
       * naehme auch den lokalen Verlauf mit. Das ist eine Entscheidung des
       * Nutzers, nicht die Folge einer Admin-Aktion.
       */
      if ((err as Error).message.includes('device_removed')) {
        setLogoutReason('device_removed');
        clearSession();
        onLogout();
        return;
      }
      setError(`${t('errFetchFailed')}: ${(err as Error).message}`);
    }
  }, [session, refreshPreview, sendToDevices, confirmRead]);

  useEffect(() => {
    void fetchMessages();
    return openStream(session.token, () => void fetchMessages());
  }, [session, fetchMessages]);

  // Push einrichten, falls die App in einem Capacitor-Container laeuft und der
  // Server ihn anbietet. Im Browser passiert hier nichts.
  useEffect(() => {
    void setUpPush(session.token);
  }, [session.token]);

  /* ── Gesprächswechsel ─────────────────────────────────────────────────── */

  useEffect(() => {
    if (!peer) return;
    void loadConversation(peer.accountId).then(setMessages);
    // Beim Oeffnen alles bestaetigen, was waehrend der Abwesenheit eintraf.
    void confirmRead(peer);
    // Die Sicherheitsnummer gilt je Gerätepaar. Bei mehreren Geräten der
    // Gegenstelle wird die des ersten gezeigt — die übrigen sind damit NICHT
    // verifiziert. Ein vollständiger Abgleich braucht eine Ansicht pro Gerät.
    const first = peer.deviceIds[0];
    if (first) void safetyNumber(session.deviceId, first).then(setFingerprint);
    else setFingerprint(null);
    setUnread((prev) => ({ ...prev, [peer.accountId]: 0 }));
    setKeyAlert([]);
    setShowSafety(false);
    setSelected([]);
  }, [peer, session.deviceId, confirmRead]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  /* ── Senden ───────────────────────────────────────────────────────────── */

  async function send(event: React.FormEvent) {
    event.preventDefault();
    if (!peer || !draft.trim() || sending) return;

    const text = draft.trim();
    setSending(true);
    setError(null);

    try {
      // Beim ersten Mal baut sendPayload per X3DH eine Sitzung auf; danach
      // läuft alles über den Double Ratchet.
      const target = await refreshDevices(peer.accountId);

      // Eine feste ID fuer alle Kopien: Die Lesebestaetigung der Gegenseite
      // nennt genau diese, und jedes eigene Geraet muss denselben Eintrag
      // treffen. Wuerde jedes Geraet eine eigene ID vergeben, liefe die
      // Bestaetigung ins Leere.
      const messageId = crypto.randomUUID();
      const timestamp = Date.now();

      await sendToDevices(target.deviceIds, { kind: 'text', text, messageId });

      // Kopie an die eigenen anderen Geraete. Schlaegt das fehl, ist die
      // Nachricht trotzdem bei der Gegenseite — deshalb kein throw.
      if (ownDevices.current.length > 0) {
        await sendToDevices(ownDevices.current, {
          kind: 'sync',
          peerAccountId: peer.accountId,
          messageId,
          text,
          timestamp,
        }).catch(() => {
          setError(t('errSyncPartial'));
        });
      }

      await saveMessage({
        id: messageId,
        peerDeviceId: peer.accountId,
        direction: 'out',
        text,
        timestamp,
        status: 'sent',
      });

      setMessages(await loadConversation(peer.accountId));
      await refreshPreview(peer.accountId);
      setDraft('');
    } catch (err) {
      if (err instanceof IdentityChangedError) {
        // Dauerhaft in den Verlauf, nicht nur als Meldung: Eine Warnung, die
        // beim naechsten Laden verschwindet, ist keine Warnung.
        await saveMessage({
          id: crypto.randomUUID(),
          peerDeviceId: peer.accountId,
          direction: 'in',
          system: true,
          text: t('safetyNumberChangedBody'),
          timestamp: Date.now(),
        });
        setMessages(await loadConversation(peer.accountId));
        setKeyAlert((prev) => (prev.includes(err.deviceId) ? prev : [...prev, err.deviceId]));
        setError(null);
      } else {
        setError(`${t('errSendFailed')}: ${(err as Error).message}`);
      }
    } finally {
      setSending(false);
    }
  }

  /** Geänderten Schlüssel nach ausdrücklicher Bestätigung annehmen. */
  async function confirmKeyChange() {
    for (const deviceId of keyAlert) await acceptIdentityChange(deviceId);
    setKeyAlert([]);
    setError(null);
  }

  /*
   * Langes Druecken auf dem Telefon, Klick am Rechner.
   *
   * Die Unterscheidung ist noetig, weil ein kurzes Antippen beim Lesen
   * staendig vorkommt — ein Menue, das dabei aufspringt, stoert mehr, als es
   * nuetzt. Am Rechner gibt es das Problem nicht: Dort klickt niemand
   * versehentlich auf eine Nachricht.
   */
  const pressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastPointer = useRef<string>('mouse');
  const longPressed = useRef(false);

  function toggleSelected(id: string) {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function startPress(id: string, pointerType: string) {
    lastPointer.current = pointerType;
    if (pointerType !== 'touch') return;

    longPressed.current = false;
    pressTimer.current = setTimeout(() => {
      longPressed.current = true;
      toggleSelected(id);
    }, 500);
  }

  function cancelPress() {
    if (!pressTimer.current) return;
    clearTimeout(pressTimer.current);
    pressTimer.current = null;
  }

  function clickMessage(id: string) {
    /*
     * Nach dem langen Druecken schickt der Browser trotzdem noch einen Klick.
     * Der traefe dieselbe Nachricht und naehme die eben getroffene Auswahl
     * sofort wieder zurueck — sichtbar waere davon nichts als eine Auswahl,
     * die nicht funktioniert.
     */
    if (longPressed.current) {
      longPressed.current = false;
      return;
    }

    /*
     * Auf Beruehrung beginnt die Auswahl nur ueber langes Druecken — sonst
     * spraenge sie beim Lesen staendig an. Ist aber schon etwas ausgewaehlt,
     * genuegt ein Tippen, um weitere hinzuzunehmen. Genau so verhaelt sich
     * eine Telefon-App, und genau das erwartet man dann auch.
     */
    if (lastPointer.current === 'touch' && selected.length === 0) return;
    toggleSelected(id);
  }

  /**
   * Ausgewählte Nachrichten in die Zwischenablage.
   *
   * In der Reihenfolge des Verlaufs, nicht in der des Antippens — wer drei
   * Zeilen kopiert, will sie so lesen, wie sie geschrieben wurden.
   */
  async function copySelected() {
    const text = messages
      .filter((m) => selected.includes(m.id))
      .map((m) => m.text)
      .join('\n');

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      setSelected([]);
    } catch {
      // Ohne Berechtigung oder in unsicherem Kontext geht es nicht. Kein
      // Fehlerbanner dafuer — es erscheint schlicht keine Bestaetigung.
    }
  }

  /* ── Abgeleitete Daten ────────────────────────────────────────────────── */

  const visibleContacts = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = needle
      ? contacts.filter((c) => c.handle.toLowerCase().includes(needle))
      : contacts;

    // Neueste Unterhaltung oben, wie in gängigen Messengern.
    return [...filtered].sort((a, b) => {
      const ta = previews[a.accountId]?.timestamp ?? 0;
      const tb = previews[b.accountId]?.timestamp ?? 0;
      return tb - ta || a.handle.localeCompare(b.handle);
    });
  }, [contacts, query, previews]);

  const grouped = useMemo(() => {
    const out: Array<{ day: string; items: StoredMessage[] }> = [];
    for (const message of messages) {
      const day = formatDay(message.timestamp, t);
      const last = out[out.length - 1];
      if (last?.day === day) last.items.push(message);
      else out.push({ day, items: [message] });
    }
    return out;
  }, [messages]);

  /* ── Darstellung ──────────────────────────────────────────────────────── */

  if (showSettings) {
    return (
      <Settings
        session={session}
        onClose={() => setShowSettings(false)}
        onLogout={onLogout}
      />
    );
  }

  return (
    <div className={peer ? 'app' : 'app show-list'}>
      <CallOverlay
        info={call.info}
        localStream={call.localStream}
        remoteStream={call.remoteStream}
        onAccept={() => void call.accept()}
        onHangUp={() => void call.hangUp('hangup')}
        onReject={() => void call.hangUp('reject')}
        onToggleMute={call.toggleMute}
        onSwitchCamera={() => void call.switchCamera()}
        onToggleCamera={call.toggleCamera}
        onMinimise={call.setMinimised}
      />
      <aside className="sidebar">
        <div className="sidebar-head">
          <h1>{t('chats')}</h1>
        </div>

        <div className="sidebar-search">
          <input
            className="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('search')}
            autoComplete="off"
          />
        </div>

        <div className="chats">
          {visibleContacts.length === 0 ? (
            <p className="empty">
              {contacts.length === 0
                ? t('noContacts')
                : t('noContactsFound')}
            </p>
          ) : (
            visibleContacts.map((contact) => {
              const preview = previews[contact.accountId];
              const count = unread[contact.accountId] ?? 0;

              return (
                <button
                  key={contact.accountId}
                  className={[
                    'chat-item',
                    peer?.accountId === contact.accountId ? 'is-active' : '',
                    count > 0 ? 'has-unread' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => setPeer(contact)}
                >
                  <Avatar name={contact.handle} />
                  <span className="body">
                    <span className="line">
                      <span className="title">{contact.handle}</span>
                      {preview && (
                        <span className="when">{formatListTime(preview.timestamp)}</span>
                      )}
                    </span>
                    <span className="row2">
                      <span className="preview">
                        {preview
                          ? `${preview.direction === 'out' ? t('youPrefix') : ''}${preview.text}`
                          : t('noMessagesYet')}
                      </span>
                      {count > 0 && <span className="badge-unread">{count}</span>}
                    </span>
                  </span>
                </button>
              );
            })
          )}
        </div>

        {/* Eigener Zugang unten links, wie im Entwurf: Die Liste oben ist der
            Arbeitsbereich, das eigene Konto eine Randnotiz. Die Einstellungen
            gehoeren dorthin, weil sie ausschliesslich dieses Konto und dieses
            Geraet betreffen. */}
        <button
          type="button"
          className="me"
          onClick={() => setShowSettings(true)}
          title={t('settings')}
        >
          <Avatar name={session.handle} small />
          <span className="me-text">
            <span className="name">{session.handle}</span>
            <span className="sub">{t('thisDevice')}</span>
          </span>
          <span className="me-gear" aria-hidden="true">
            <SettingsIcon />
          </span>
        </button>
      </aside>

      <main className="thread">
        {!peer ? (
          <div className="thread-empty">
            <span>
              {t('pickContact')}
            </span>
          </div>
        ) : (
          <>
            {/*
              Die Kopfzeile hat zwei Gestalten. Ist etwas ausgewaehlt, tritt
              die Auswahlleiste an ihre Stelle — dieselbe Zeile, andere
              Knoepfe. Das ist der Griff, den Telefon-Apps benutzen: Die
              Aktionen sitzen dort, wo immer Aktionen sitzen, statt als Reihe
              unter der Blase aufzutauchen und den Verlauf zu verschieben.
            */}
            {selected.length > 0 ? (
              <header className="thread-head is-selecting">
                <button
                  className="icon-btn back"
                  onClick={() => setSelected([])}
                  aria-label={t('cancelSelection')}
                >
                  <CloseIcon />
                </button>
                <h2 className="select-count">{selected.length}</h2>
                <span className="spacer" />
                <button
                  className="icon-btn"
                  onClick={() => void copySelected()}
                  title={t('copy')}
                  aria-label={t('copy')}
                >
                  <CopyIcon />
                </button>
              </header>
            ) : (
            <header className="thread-head">
              <button
                className="icon-btn back"
                onClick={() => setPeer(null)}
                aria-label={t('back')}
              >
                <BackIcon />
              </button>
              <Avatar name={peer.handle} small />
              {/* Die Verschlüsselungszeile ist der Zugang zur
                  Sicherheitsnummer. Vorher lag sie unter dem Verlauf, wo man
                  scrollen musste, um sie zu finden — dabei ist sie das
                  einzige Mittel, mit dem sich prüfen lässt, ob die Zusage
                  „Ende-zu-Ende verschlüsselt" überhaupt gilt. Sie gehört
                  neben diese Zusage, nicht weit darunter. */}
              <button
                type="button"
                className="thread-peer"
                onClick={() => setShowSafety((open) => !open)}
                disabled={!fingerprint}
                title={fingerprint ? t('compareSafetyNumber') : undefined}
              >
                <h2>{peer.handle}</h2>
                <span className="sub">
                  <ShieldIcon />
                  {t('endToEndEncrypted')}
                  {fingerprint && <span className="thread-peer-caret">{showSafety ? '▴' : '▾'}</span>}
                </span>
              </button>
              <span className="spacer" />

              {/* Es klingeln alle Geräte der Gegenstelle; wer zuerst
                  annimmt, bekommt das Gespräch. */}
              <button
                className="icon-btn"
                onClick={() => void startCall(peer, false)}
                disabled={call.info.state !== 'idle' || peer.deviceIds.length === 0}
                title={t('voiceCall')}
                aria-label={t('voiceCall')}
              >
                <PhoneIcon />
              </button>
              <button
                className="icon-btn"
                onClick={() => void startCall(peer, true)}
                disabled={call.info.state !== 'idle' || peer.deviceIds.length === 0}
                title={t('videoCall')}
                aria-label={t('videoCall')}
              >
                <VideoIcon />
              </button>
            </header>
            )}


            {showSafety && fingerprint && (
              <div className="safety-panel">
                <h3>{t('compareSafetyNumber')}</h3>
                {/* In Blöcken statt als eine lange Zahl: Vorlesen und
                    Abgleichen ist genau das, wofür sie da ist. */}
                <div className="safety-number">
                  {fingerprint.replace(/\s+/g, '').match(/.{1,5}/g)?.map((group, i) => (
                    <span key={i}>{group}</span>
                  ))}
                </div>
                <p className="hint" style={{ marginBottom: 0 }}>
                  {t('safetyNumberHint')}
                </p>
              </div>
            )}

            {error && <div className="alert alert-error">{error}</div>}

            {/* Kurze Rueckmeldung statt eines Knopfes, der seine
                Beschriftung wechselt: Der Knopf ist nach dem Kopieren weg,
                weil die Auswahl aufgehoben wird. */}
            {copied && <div className="toast">{t('copied')}</div>}

            <div className="messages">
              {grouped.map((group) => (
                <div key={group.day} style={{ display: 'contents' }}>
                  <div className="day">{group.day}</div>
                  {group.items.map((message) =>
                    message.system ? (
                      // Systemeintraege sitzen mittig und tragen keine Blase:
                      // Sie kommen von niemandem, sie stellen etwas fest.
                      <div key={message.id} className="msg-system">
                        {message.text}
                      </div>
                    ) : (
                    /*
                      Die Zeile umschliesst die Blase, damit die Auswahl ueber
                      die volle Breite liegt und nicht nur so weit reicht wie
                      der Text. Eine halbhohe Markierung sieht aus wie ein
                      Darstellungsfehler; die durchgehende sagt: diese Zeile
                      ist gemeint.
                    */
                    <div
                      key={message.id}
                      className={[
                        'msg-row',
                        message.direction === 'out' ? 'msg-row-out' : 'msg-row-in',
                        selected.includes(message.id) ? 'is-selected' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => {
                        // Irgendwo neben die Blase tippen waehlt die Zeile
                        // ebenfalls ab — solange ueberhaupt etwas ausgewaehlt
                        // ist. Sonst passiert hier nichts.
                        if (selected.length > 0) clickMessage(message.id);
                      }}
                    >
                    <div className={message.direction === 'out' ? 'msg msg-out' : 'msg msg-in'}>
                      {/*
                        Die Blase SELBST ist der Knopf, nicht ein Element
                        darin. Ein Blockelement im Inneren hat den nach rechts
                        schwebenden Zeitstempel verdraengt — der Text muss
                        Inline-Inhalt bleiben, damit er um ihn herumfliesst.
                      */}
                      <button
                        type="button"
                        className="bubble"
                        onPointerDown={(e) => startPress(message.id, e.pointerType)}
                        onPointerUp={cancelPress}
                        onPointerLeave={cancelPress}
                        onPointerCancel={cancelPress}
                        onPointerMove={cancelPress}
                        onClick={(e) => {
                          e.stopPropagation();
                          clickMessage(message.id);
                        }}
                        onContextMenu={(e) => {
                          // Auf dem Telefon soll das lange Druecken unser Menue
                          // oeffnen, nicht das des Systems.
                          if (lastPointer.current === 'touch') e.preventDefault();
                        }}
                      >
                        <span className="meta">
                          {formatTime(message.timestamp)}
                          {/* Ein Haken: zugestellt. Zwei: von der Gegenseite
                              geöffnet. Die Bestätigung ist eine eigene
                              verschlüsselte Nachricht — der Server sieht also
                              zusätzlichen Verkehr in die Gegenrichtung und
                              kann daran ablesen, wann jemand mitliest.
                              Siehe docs/03-bedrohungsmodell.md. */}
                          {message.direction === 'out' && (
                            <span
                              className={message.status === 'read' ? 'tick is-read' : 'tick'}
                              title={message.status === 'read' ? t('read') : t('delivered')}
                            >
                              <CheckIcon double={message.status === 'read'} />
                            </span>
                          )}
                        </span>
                        {message.text}
                      </button>
                    </div>
                    </div>
                    ),
                  )}
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            {keyAlert.length > 0 && (
              <div className="key-alert">
                <strong>{t('safetyNumberChanged')}</strong>
                <p>
                  {t('keyAlertLead')}
                </p>
                <button type="button" className="btn btn-sm" onClick={() => void confirmKeyChange()}>
                  {t('acceptNewKey')}
                </button>
              </div>
            )}

            <form className="composer" onSubmit={send}>
              <input
                className="field"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={t('writeMessage')}
                disabled={sending}
                autoComplete="off"
              />
              <button
                className="send"
                type="submit"
                disabled={sending || !draft.trim()}
                aria-label={t('send')}
              >
                <SendIcon />
              </button>
            </form>
          </>
        )}
      </main>
    </div>
  );
}
